import { useApi } from '~/api'
import { defineService } from '~/api/models/helpers'
import type { AuthArgs } from '~/api/models/index'
import type { WithId } from '~/types/common'
import type { ConfigurationPresetPayload } from '~/types/configurationPreset'
import { isGroupPresetEnabled } from '~/composables/useFeatureFlags'

const PRESET_CACHE_KEYS = [
  ['allConfigurationPresets'],
  ['groupDetails'],
  ['groupPresetStatus'],
  ['assetById'],
  ['allGroups']
]

const queries = {
  getAll: {
    queryFn: () => useApi().configurationPresets.getConfigurationPresets(),
    queryKey: () => ['allConfigurationPresets'],
    options: {
      enabled: () => isGroupPresetEnabled
    }
  },
  getByAssetType: {
    queryFn: (args: Ref<{ assetType: string }>) =>
      useApi().configurationPresets.getConfigurationPresets(args.value.assetType),
    queryKey: (args: Ref<{ assetType: string }>) => ['allConfigurationPresets', args.value.assetType],
    options: {
      enabled: (args: Ref<{ assetType: string }>) => isGroupPresetEnabled && Boolean(args.value.assetType)
    }
  }
} as const

const mutations = {
  create: {
    request: (payload: ConfigurationPresetPayload) => useApi().configurationPresets.createConfigurationPreset(payload),
    cacheKey: () => PRESET_CACHE_KEYS
  },
  update: {
    request: ({ id, ...payload }: WithId<ConfigurationPresetPayload>) =>
      useApi().configurationPresets.updateConfigurationPreset(id, payload),
    cacheKey: () => PRESET_CACHE_KEYS
  },
  delete: {
    request: ({ id, force }: { id: number; force?: boolean }) =>
      useApi().configurationPresets.deleteConfigurationPreset(id, force),
    cacheKey: () => PRESET_CACHE_KEYS
  }
} as const

export type ConfigurationPresetService = ReturnType<typeof defineService<typeof queries, typeof mutations>>

export const registerService = (authArgs: AuthArgs): ConfigurationPresetService =>
  defineService(authArgs, {
    queries,
    mutations
  })
